import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import {
  Truck, Plus, Search, X, ChevronDown, AlertTriangle,
  Clock, CheckCircle, Edit2, Trash2, Phone, RefreshCw,
  Building2, User, Calendar, Hash, ArrowDownCircle, TrendingUp,
  ChevronRight, XCircle
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = d => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};
const today = () => new Date().toISOString().split('T')[0];

function daysDiff(dateStr) {
  if (!dateStr) return null;
  const diff = Math.floor((new Date(dateStr) - new Date()) / 86400000);
  return diff;
}

// ── Searchable Select ─────────────────────────────────────────────────────────
// Generic, dependency-free searchable dropdown.
// options: [{ value, label }]  — include an explicit empty-value option for "All / None"
function SearchableSelect({ value, onChange, options, placeholder = 'Select...', disabled = false }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(''); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find(o => String(o.value) === String(value));
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter(o => String(o.label).toLowerCase().includes(q)) : options;

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <input
          className="w-full border border-gray-200 rounded-lg pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white cursor-pointer disabled:bg-gray-50 disabled:cursor-not-allowed"
          placeholder={placeholder}
          value={open ? query : (selected ? selected.label : '')}
          disabled={disabled}
          onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
        />
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-400">No results found</p>
          ) : filtered.map((o, i) => (
            <button
              key={i} type="button"
              className={`w-full text-left px-3 py-2 text-sm hover:bg-amber-50 transition-colors ${
                String(o.value) === String(value) ? 'bg-amber-50 text-amber-700 font-semibold' : 'text-gray-700'
              }`}
              onClick={() => { onChange(o.value); setOpen(false); setQuery(''); }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function JobStatus({ status, expected_date }) {
  const isOverdue = status !== 'Received' && status !== 'Cancelled'
    && expected_date && new Date(expected_date) < new Date();
  const isDueToday = status !== 'Received' && status !== 'Cancelled'
    && expected_date && expected_date === today();

  if (status === 'Received') return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
      <CheckCircle className="w-3 h-3" /> Received
    </span>
  );
  if (status === 'Cancelled') return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
      <XCircle className="w-3 h-3" /> Cancelled
    </span>
  );
  if (isOverdue) return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
      <AlertTriangle className="w-3 h-3" /> {status} · Overdue
    </span>
  );
  if (isDueToday) return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
      <Clock className="w-3 h-3" /> {status} · Due Today
    </span>
  );
  if (status === 'Partial') return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
      <TrendingUp className="w-3 h-3" /> Partial
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
      <Truck className="w-3 h-3" /> Sent
    </span>
  );
}

function RowColor(job) {
  if (job.status === 'Received' || job.status === 'Cancelled') return '';
  const isOverdue = job.expected_date && new Date(job.expected_date) < new Date();
  const isDueToday = job.expected_date === today();
  if (isOverdue) return 'bg-red-50/60 border-l-4 border-red-400';
  if (isDueToday) return 'bg-amber-50/60 border-l-4 border-amber-400';
  return 'border-l-4 border-transparent';
}

// ── Vendor Autocomplete ───────────────────────────────────────────────────────
function VendorInput({ value, phone, onChange, onPhoneChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setShowSug(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = async (q) => {
    if (!q || q.length < 1) { setSuggestions([]); return; }
    try {
      const res = await api.get(`/outsource-jobs/vendors/suggestions?q=${encodeURIComponent(q)}`);
      setSuggestions(res.data);
      setShowSug(true);
    } catch { /* ignore */ }
  };

  return (
    <div ref={ref} className="relative">
      <input
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
        placeholder="Enter vendor name..."
        value={value}
        onChange={e => { onChange(e.target.value); search(e.target.value); }}
        onFocus={() => value && search(value)}
        required
      />
      {showSug && suggestions.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={i} type="button"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-amber-50 text-left"
              onClick={() => {
                onChange(s.vendor_name);
                if (s.vendor_phone) onPhoneChange(s.vendor_phone);
                setShowSug(false);
              }}
            >
              <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <div>
                <div className="font-medium text-gray-800">{s.vendor_name}</div>
                {s.vendor_phone && <div className="text-xs text-gray-500">{s.vendor_phone} · {s.job_count} job{s.job_count>1?'s':''}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Job Form Modal ────────────────────────────────────────────────────────────
function JobFormModal({ job, projects, departments, onClose, onSaved }) {
  const [form, setForm] = useState({
    project_id: job?.project_id || '',
    project_item_id: job?.project_item_id || '',
    department: job?.department || '',
    vendor_name: job?.vendor_name || '',
    vendor_phone: job?.vendor_phone || '',
    qty_sent: job?.qty_sent || '',
    qty_received: job?.qty_received || 0,
    sent_date: job?.sent_date || today(),
    expected_date: job?.expected_date || '',
    received_date: job?.received_date || '',
    status: job?.status || 'Sent',
    notes: job?.notes || '',
  });
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (form.project_id) {
      api.get(`/projects/${form.project_id}`).then(r => {
        setItems(r.data.items || []);
      }).catch(() => setItems([]));
    } else {
      setItems([]);
    }
  }, [form.project_id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Project options come straight from the projects list (required → no empty option)
  const projectOptions = useMemo(
    () => projects.map(p => ({ value: p.id, label: `${p.project_id} — ${p.name}` })),
    [projects]
  );

  // Item options for the selected project
  const itemOptions = useMemo(
    () => [{ value: '', label: '-- Select Item --' }, ...items.map(i => ({ value: i.id, label: i.item_name }))],
    [items]
  );

  // Department options come from the actual Departments master.
  // If this job carries a department that is no longer active, keep it visible so the value isn't lost.
  const deptOptions = useMemo(() => {
    const opts = [{ value: '', label: '-- Select Department --' }];
    const names = new Set();
    departments.forEach(d => { opts.push({ value: d.name, label: d.name }); names.add(d.name); });
    if (form.department && !names.has(form.department)) {
      opts.push({ value: form.department, label: `${form.department} (inactive)` });
    }
    return opts;
  }, [departments, form.department]);

  const statusOptions = ['Sent', 'Partial', 'Received', 'Cancelled'].map(s => ({ value: s, label: s }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.project_id) { toast.error('Please select a project'); return; }
    if (!form.vendor_name.trim()) { toast.error('Vendor name is required'); return; }
    setSaving(true);
    try {
      if (job?.id) {
        await api.put(`/outsource-jobs/${job.id}`, form);
        toast.success('Job updated');
      } else {
        await api.post('/outsource-jobs', form);
        toast.success('New outsource job added');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <Truck className="w-4 h-4 text-amber-600" />
            </div>
            <h2 className="font-bold text-gray-800">{job ? 'Edit Job' : 'New Outsource Job'}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Project + Item */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Project *</label>
              <SearchableSelect
                value={form.project_id}
                onChange={v => { set('project_id', v); set('project_item_id', ''); }}
                options={projectOptions}
                placeholder="-- Select Project --"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Item</label>
              <SearchableSelect
                value={form.project_item_id}
                onChange={v => set('project_item_id', v)}
                options={itemOptions}
                placeholder="-- Select Item --"
                disabled={!form.project_id}
              />
            </div>
          </div>

          {/* Department */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Department</label>
            <SearchableSelect
              value={form.department}
              onChange={v => set('department', v)}
              options={deptOptions}
              placeholder="-- Select Department --"
            />
          </div>

          {/* Vendor */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Vendor Name *</label>
            <VendorInput
              value={form.vendor_name}
              phone={form.vendor_phone}
              onChange={v => set('vendor_name', v)}
              onPhoneChange={v => set('vendor_phone', v)}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Vendor Phone</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="Optional"
              value={form.vendor_phone}
              onChange={e => set('vendor_phone', e.target.value)}
              type="tel"
            />
          </div>

          {/* Qty */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Qty Sent *</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                type="number" min="1"
                value={form.qty_sent}
                onChange={e => set('qty_sent', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Qty Received</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                type="number" min="0"
                value={form.qty_received}
                onChange={e => set('qty_received', e.target.value)}
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Sent Date *</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                type="date"
                value={form.sent_date}
                onChange={e => set('sent_date', e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Expected Date *</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                type="date"
                value={form.expected_date}
                onChange={e => set('expected_date', e.target.value)}
                required
              />
            </div>
          </div>

          {job && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Received Date</label>
                <input
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  type="date"
                  value={form.received_date || ''}
                  onChange={e => set('received_date', e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Status</label>
                <SearchableSelect
                  value={form.status}
                  onChange={v => set('status', v)}
                  options={statusOptions}
                  placeholder="Select status"
                />
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Notes</label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
              rows={2}
              placeholder="Any extra info..."
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold transition-colors disabled:opacity-60"
            >
              {saving ? 'Saving...' : (job ? 'Update' : 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Quick Receive Modal ────────────────────────────────────────────────────────
function QuickReceiveModal({ job, onClose, onSaved }) {
  const [qty, setQty] = useState(job.qty_sent - job.qty_received);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const pending = job.qty_sent - job.qty_received;

  const handle = async () => {
    setSaving(true);
    try {
      await api.patch(`/outsource-jobs/${job.id}/receive`, {
        qty_received: job.qty_received + qty,
        notes
      });
      toast.success('Received quantity updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <ArrowDownCircle className="w-4 h-4 text-emerald-600" />
            </div>
            <h2 className="font-bold text-gray-800">Receive Update</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-600">
            <p className="font-semibold text-gray-800">{job.vendor_name}</p>
            <p className="mt-0.5">{job.project_name} {job.item_name ? `→ ${job.item_name}` : ''}</p>
            <div className="flex gap-4 mt-2 text-xs">
              <span>Sent: <strong>{job.qty_sent}</strong></span>
              <span>Already received: <strong>{job.qty_received}</strong></span>
              <span className="text-amber-600">Pending: <strong>{pending}</strong></span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Quantity received now (max {pending})</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              type="number" min="1" max={pending}
              value={qty}
              onChange={e => setQty(Math.min(parseInt(e.target.value)||0, pending))}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Notes</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="Optional..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={handle} disabled={saving || qty <= 0}
              className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold disabled:opacity-60"
            >
              {saving ? 'Saving...' : `Receive ${qty} pcs`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Summary Cards ─────────────────────────────────────────────────────────────
function SummaryCards({ jobs }) {
  const pending  = jobs.filter(j => !['Received','Cancelled'].includes(j.status));
  const overdue  = pending.filter(j => j.expected_date && new Date(j.expected_date) < new Date());
  const partial  = jobs.filter(j => j.status === 'Partial');
  const received = jobs.filter(j => j.status === 'Received');

  const cards = [
    { label: 'Total Pending', value: pending.length, color: '#7C3AED', bg: '#F5F3FF', icon: Truck },
    { label: 'Overdue', value: overdue.length, color: '#DC2626', bg: '#FEF2F2', icon: AlertTriangle },
    { label: 'Partial', value: partial.length, color: '#D97706', bg: '#FFFBEB', icon: TrendingUp },
    { label: 'Received', value: received.length, color: '#059669', bg: '#ECFDF5', icon: CheckCircle },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: c.bg }}>
            <c.icon className="w-5 h-5" style={{ color: c.color }} />
          </div>
          <div>
            <p className="text-2xl font-black text-gray-900">{c.value}</p>
            <p className="text-xs text-gray-500 font-medium">{c.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function OutsourceTrackerPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess } = usePermission('outsource');
  const [jobs, setJobs]       = useState([]);
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editJob, setEditJob]   = useState(null);
  const [receiveJob, setReceiveJob] = useState(null);
  const [search, setSearch]    = useState('');
  const [filters, setFilters]  = useState({ status: '', department: '', project_id: '', overdue: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status)     params.set('status', filters.status);
      if (filters.department) params.set('department', filters.department);
      if (filters.project_id) params.set('project_id', filters.project_id);
      if (filters.overdue)    params.set('overdue', '1');

      const [jobsRes, projRes, deptRes] = await Promise.all([
        api.get(`/outsource-jobs?${params}`),
        api.get('/projects?status=active'),
        api.get('/departments?active_only=1'),
      ]);
      setJobs(jobsRes.data);
      setProjects(projRes.data);
      setDepartments(deptRes.data || []);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { if (hasAccess) load(); else setLoading(false); }, [load, hasAccess]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this job?')) return;
    try {
      await api.delete(`/outsource-jobs/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = jobs.filter(j => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      j.vendor_name?.toLowerCase().includes(s) ||
      j.project_name?.toLowerCase().includes(s) ||
      j.item_name?.toLowerCase().includes(s) ||
      j.department?.toLowerCase().includes(s) ||
      j.proj_code?.toLowerCase().includes(s)
    );
  });

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({ status: '', department: '', project_id: '', overdue: '' });
  const hasFilters = Object.values(filters).some(v => v);

  // ── Filter dropdown options (each includes an explicit "All" entry) ──────────
  const statusFilterOptions = [
    { value: '', label: 'All Status' },
    { value: 'Sent', label: 'Sent' },
    { value: 'Partial', label: 'Partial' },
    { value: 'Received', label: 'Received' },
    { value: 'Cancelled', label: 'Cancelled' },
  ];
  const deptFilterOptions = useMemo(() => {
    const opts = [{ value: '', label: 'All Departments' }];
    const names = new Set();
    departments.forEach(d => { opts.push({ value: d.name, label: d.name }); names.add(d.name); });
    // Keep an active filter value visible even if its department was deactivated
    if (filters.department && !names.has(filters.department)) {
      opts.push({ value: filters.department, label: `${filters.department} (inactive)` });
    }
    return opts;
  }, [departments, filters.department]);
  const projectFilterOptions = useMemo(
    () => [{ value: '', label: 'All Projects' }, ...projects.map(p => ({ value: p.id, label: `${p.project_id} — ${p.name}` }))],
    [projects]
  );

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">
            <Truck className="w-6 h-6 text-amber-500" />
            Outsource Tracker
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Work sent to vendors — all in one place</p>
        </div>
        <CanWrite canWrite={canWrite}>
          <button
            onClick={() => { setEditJob(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm rounded-xl transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" /> New Job
          </button>
        </CanWrite>
      </div>

      {/* Summary */}
      <SummaryCards jobs={jobs} />

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="Search vendor, project or item..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="w-40">
            <SearchableSelect
              value={filters.status}
              onChange={v => setFilter('status', v)}
              options={statusFilterOptions}
              placeholder="All Status"
            />
          </div>

          <div className="w-48">
            <SearchableSelect
              value={filters.department}
              onChange={v => setFilter('department', v)}
              options={deptFilterOptions}
              placeholder="All Departments"
            />
          </div>

          <div className="w-64">
            <SearchableSelect
              value={filters.project_id}
              onChange={v => setFilter('project_id', v)}
              options={projectFilterOptions}
              placeholder="All Projects"
            />
          </div>

          <button
            onClick={() => setFilter('overdue', filters.overdue ? '' : '1')}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
              filters.overdue ? 'bg-red-100 text-red-700 border border-red-300' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            Overdue Only
          </button>

          {hasFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" /> Clear
            </button>
          )}

          <button onClick={load} className="ml-auto text-gray-400 hover:text-gray-600 transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mr-3" />
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Truck className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-semibold">No outsource jobs found</p>
          <p className="text-sm mt-1">Use the New Job button to add one</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(job => {
            const pending = job.qty_sent - job.qty_received;
            const pct = job.qty_sent > 0 ? Math.round((job.qty_received / job.qty_sent) * 100) : 0;
            const diff = daysDiff(job.expected_date);
            const isActive = !['Received', 'Cancelled'].includes(job.status);
            return (
              <div
                key={job.id}
                className={`bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden ${RowColor(job)}`}
              >
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Left info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <JobStatus status={job.status} expected_date={job.expected_date} />
                        {job.department && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            <Building2 className="w-3 h-3" />{job.department}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700">
                        <span className="flex items-center gap-1.5 font-semibold">
                          <User className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                          {job.vendor_name}
                          {job.vendor_phone && (
                            <a href={`tel:${job.vendor_phone}`} className="text-xs text-blue-500 hover:underline">
                              <Phone className="w-3 h-3 inline" /> {job.vendor_phone}
                            </a>
                          )}
                        </span>
                        <span className="flex items-center gap-1 text-gray-600">
                          <Hash className="w-3.5 h-3.5" />
                          {job.proj_code || job.project_name}
                          {job.item_name && <><ChevronRight className="w-3 h-3" />{job.item_name}</>}
                        </span>
                      </div>

                      {/* Qty progress */}
                      <div className="mt-2 flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Received: <strong className="text-gray-800">{job.qty_received}</strong> / {job.qty_sent}</span>
                            {isActive && pending > 0 && <span className="text-amber-600 font-semibold">{pending} pending</span>}
                          </div>
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${pct}%`,
                                background: pct === 100 ? '#10B981' : pct > 0 ? '#F59E0B' : '#CBD5E1'
                              }}
                            />
                          </div>
                        </div>
                        <span className="text-xs font-bold text-gray-500 w-8 text-right">{pct}%</span>
                      </div>

                      {/* Dates */}
                      <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Sent: {fmt(job.sent_date)}
                        </span>
                        <span className={`flex items-center gap-1 ${
                          isActive && diff < 0 ? 'text-red-600 font-semibold' : 
                          isActive && diff === 0 ? 'text-amber-600 font-semibold' : ''
                        }`}>
                          <Clock className="w-3 h-3" />
                          Expected: {fmt(job.expected_date)}
                          {isActive && diff !== null && (
                            <span className="ml-1">
                              {diff < 0 ? `(${Math.abs(diff)}d overdue)` : diff === 0 ? '(Today!)' : `(${diff}d left)`}
                            </span>
                          )}
                        </span>
                        {job.received_date && (
                          <span className="flex items-center gap-1 text-emerald-600">
                            <CheckCircle className="w-3 h-3" />
                            Received: {fmt(job.received_date)}
                          </span>
                        )}
                      </div>

                      {job.notes && (
                        <p className="mt-2 text-xs text-gray-500 italic">{job.notes}</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      {isActive && pending > 0 && (
                        <CanEdit canEdit={canEdit}>
                          <button
                            onClick={() => setReceiveJob(job)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-lg transition-colors"
                          >
                            <ArrowDownCircle className="w-3.5 h-3.5" /> Receive
                          </button>
                        </CanEdit>
                      )}
                      <CanEdit canEdit={canEdit}>
                        <button
                          onClick={() => { setEditJob(job); setShowForm(true); }}
                          className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs font-semibold rounded-lg transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" /> Edit
                        </button>
                      </CanEdit>
                      <CanDelete canDelete={canDelete}>
                        <button
                          onClick={() => handleDelete(job.id)}
                          className="flex items-center gap-1 px-3 py-1.5 border border-red-100 text-red-500 hover:bg-red-50 text-xs font-semibold rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </CanDelete>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <JobFormModal
          job={editJob}
          projects={projects}
          departments={departments}
          onClose={() => { setShowForm(false); setEditJob(null); }}
          onSaved={() => { setShowForm(false); setEditJob(null); load(); }}
        />
      )}

      {receiveJob && (
        <QuickReceiveModal
          job={receiveJob}
          onClose={() => setReceiveJob(null)}
          onSaved={() => { setReceiveJob(null); load(); }}
        />
      )}
    </div>
    </PermissionGuard>
  );
}
